import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, Users, Car, FileText, Receipt, Wrench, Package, 
  Settings, LogOut, ChevronLeft, ChevronRight, Menu, X, Eye,
  TrendingUp, AlertCircle, Clock, Plus, Search, Bell, Mail
} from 'lucide-react';

// Sub-components
import CRMDashboardHome from '../../components/crm/CRMDashboardHome';
import ClientsModule from '../../components/crm/ClientsModule';
import ClientVehiclesModule from '../../components/crm/ClientVehiclesModule';
import QuotesModule from '../../components/crm/QuotesModule';
import InvoicesModule from '../../components/crm/InvoicesModule';
import InterventionsModule from '../../components/crm/InterventionsModule';
import ProductsModule from '../../components/crm/ProductsModule';
import VehiclesSaleModule from '../../components/crm/VehiclesSaleModule';
import EmailHistoryModule from '../../components/crm/EmailHistoryModule';
import OverdueInvoicesModule from '../../components/crm/OverdueInvoicesModule';
import SettingsModule from '../../components/crm/SettingsModule';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
  { id: 'clients', label: 'Clients', icon: Users },
  { id: 'vehicles', label: 'Véhicules clients', icon: Car },
  { id: 'quotes', label: 'Devis', icon: FileText },
  { id: 'invoices', label: 'Factures', icon: Receipt },
  { id: 'overdue', label: 'Relances', icon: AlertCircle },
  { id: 'interventions', label: 'Interventions', icon: Wrench },
  { id: 'products', label: 'Pièces / Stock', icon: Package },
  { id: 'vehicles-sale', label: 'Véhicules en vente', icon: Car },
  { id: 'emails', label: 'Historique emails', icon: Mail },
  { id: 'settings', label: 'Paramètres', icon: Settings },
];

export default function CRMDashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);

  const token = typeof window !== 'undefined' ? sessionStorage.getItem('mca_auth_token') : null;
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/api/crm/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch (error) {
      setStats(null);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setMobileSidebarOpen(false);
  };

  const renderContent = () => {
    const props = { authHeaders, stats, refreshStats: fetchStats, onNavigate: handleTabChange };
    switch (activeTab) {
      case 'dashboard': return <CRMDashboardHome {...props} />;
      case 'clients': return <ClientsModule {...props} />;
      case 'vehicles': return <ClientVehiclesModule {...props} />;
      case 'quotes': return <QuotesModule {...props} />;
      case 'invoices': return <InvoicesModule {...props} />;
      case 'overdue': return <OverdueInvoicesModule {...props} />;
      case 'interventions': return <InterventionsModule {...props} />;
      case 'products': return <ProductsModule {...props} />;
      case 'vehicles-sale': return <VehiclesSaleModule {...props} />;
      case 'emails': return <EmailHistoryModule {...props} />;
      case 'settings': return <SettingsModule {...props} />;
      default: return <CRMDashboardHome {...props} />;
    }
  };

  return (
    <div className="flex h-screen bg-gray-50" data-testid="crm-dashboard">
      {/* Mobile overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ${
        mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      } ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-100">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#DC2626] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">M</span>
              </div>
              <span className="font-bold text-gray-900 text-sm">MCA CAR</span>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="hidden lg:block p-1 hover:bg-gray-100 rounded" data-testid="sidebar-toggle">
            {sidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
          </button>
          <button onClick={() => setMobileSidebarOpen(false)} className="lg:hidden p-1">
            <X size={20} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                  isActive ? 'bg-red-50 text-[#DC2626] border-r-3 border-[#DC2626] font-semibold' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                } ${!sidebarOpen ? 'justify-center' : ''}`}
                data-testid={`crm-nav-${tab.id}`}
                title={tab.label}
              >
                <Icon size={20} className={isActive ? 'text-[#DC2626]' : ''} />
                {sidebarOpen && <span>{tab.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="border-t border-gray-100 p-4 space-y-2">
          <a href="/admin/dashboard" className={`flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 ${!sidebarOpen ? 'justify-center' : ''}`} data-testid="crm-back-dashboard">
            <ChevronLeft size={16} />
            {sidebarOpen && <span>Dashboard Admin</span>}
          </a>
          <a href="/" target="_blank" className={`flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 ${!sidebarOpen ? 'justify-center' : ''}`}>
            <Eye size={16} />
            {sidebarOpen && <span>Voir le site</span>}
          </a>
          <button onClick={logout} className={`flex items-center gap-2 text-sm text-red-500 hover:text-red-700 ${!sidebarOpen ? 'justify-center' : ''}`} data-testid="crm-logout">
            <LogOut size={16} />
            {sidebarOpen && <span>Déconnexion</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileSidebarOpen(true)} className="lg:hidden p-2 hover:bg-gray-100 rounded" data-testid="mobile-menu-btn">
              <Menu size={20} />
            </button>
            <h1 className="text-lg font-semibold text-gray-900" data-testid="crm-page-title">
              {TABS.find(t => t.id === activeTab)?.label || 'Tableau de bord'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {stats && stats.invoices_unpaid > 0 && (
              <button onClick={() => handleTabChange('invoices')} className="flex items-center gap-1 text-xs bg-red-50 text-red-600 px-3 py-1.5 rounded-full hover:bg-red-100">
                <AlertCircle size={14} />
                {stats.invoices_unpaid} impayée{stats.invoices_unpaid > 1 ? 's' : ''}
              </button>
            )}
            <span className="text-sm text-gray-500 hidden md:block">{user?.username}</span>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6" data-testid="crm-content">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}
